import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useUser, type User } from "../../contexts/UserContext";

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Where to redirect unauthenticated users. Defaults to /account (login page). */
  redirectTo?: string;
  /** If provided, only users with these roles can access. Others get redirected to /. */
  allowedRoles?: User['role'][];
}

export default function ProtectedRoute({
  children,
  redirectTo = "/account",
  allowedRoles,
}: ProtectedRouteProps) {
  const { user, loading } = useUser();
  const location = useLocation();
  const [timedOut, setTimedOut] = useState(false);

  // If auth check takes too long, stop waiting and redirect to login
  useEffect(() => {
    if (!loading) {
      setTimedOut(false);
      return;
    }
    const t = setTimeout(() => {
      console.warn("[ProtectedRoute] Auth check timed out, redirecting to login");
      setTimedOut(true);
    }, 7000);
    return () => clearTimeout(t);
  }, [loading]);

  // Auth state still loading — show spinner
  if (loading && !timedOut) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-stone-500 text-sm">Oturum kontrol ediliyor...</p>
        </div>
      </div>
    );
  }

  // Not authenticated (or timed out) → redirect to login, preserving intended destination
  if (!user) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // Role check: if allowedRoles is specified and user's role is not in the list
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
